import * as echarts from 'echarts/core';
import {chartColor, CHART_COLORS} from './bid-dashboard-theme';

const TITLES = {
  scale: '规模分布',
  type: '类型分布',
  result: '开标结果',
  trend: '月度趋势',
};

function formatWan(value) {
  const n = Number(value) || 0;
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return `${n.toLocaleString()}元`;
}

function pick(obj, keys, fallback) {
  for (let i = 0; i < keys.length; i++) {
    const val = obj[keys[i]];
    if (val != null) return val;
  }
  return fallback;
}

function pieOption(items, title) {
  const total = items.reduce((s, v) => s + v.value, 0);
  const data = items.map((item, i) => ({
    name: item.name,
    value: item.value,
    itemStyle: {color: chartColor(i)},
  }));
  const showLegend = items.length <= 5;

  return {
    title: {
      text: title,
      left: 8,
      top: 4,
      textStyle: {fontSize: 11, fontWeight: 700, color: '#1e3a5f'},
    },
    legend: showLegend ? {
      bottom: 2,
      left: 'center',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 4,
      textStyle: {fontSize: 8, color: '#6b8aad'},
    } : {show: false},
    tooltip: {
      trigger: 'item',
      confine: true,
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#c5ddf5',
      textStyle: {fontSize: 11, color: '#1e3a5f'},
      formatter: '{b}: {c} ({d}%)',
    },
    series: [{
      type: 'pie',
      radius: showLegend ? ['40%', '62%'] : ['46%', '72%'],
      center: ['50%', showLegend ? '46%' : '56%'],
      avoidLabelOverlap: true,
      itemStyle: {
        borderRadius: 4,
        borderColor: '#fff',
        borderWidth: 2,
      },
      label: {show: false},
      labelLine: {show: false},
      data,
    }],
    graphic: [{
      type: 'text',
      left: 'center',
      top: showLegend ? '38%' : '48%',
      style: {
        text: `${total}`,
        textAlign: 'center',
        fill: '#0d4a8f',
        fontSize: 14,
        fontWeight: 800,
      },
    }, {
      type: 'text',
      left: 'center',
      top: showLegend ? '48%' : '58%',
      style: {
        text: '项',
        textAlign: 'center',
        fill: '#9cb8d8',
        fontSize: 9,
      },
    }],
  };
}

function trendComboOption(items, title) {
  const months = items.map((v) => v.name);
  const bidCounts = items.map((v) => pick(v, ['bid_count', 'bid', 'value'], 0));
  const winCounts = items.map((v) => pick(v, ['win_count', 'win'], 0));
  const winAmounts = items.map((v) => Number(pick(v, ['win_amount', 'amount'], 0)) / 10000);

  return {
    title: {
      text: title,
      left: 8,
      top: 2,
      textStyle: {fontSize: 11, fontWeight: 700, color: '#1e3a5f'},
    },
    legend: {
      top: 2,
      right: 8,
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 8,
      textStyle: {fontSize: 9, color: '#6b8aad'},
      data: ['参与投标数', '中标数', '中标金额'],
    },
    grid: {left: 36, right: 42, top: 28, bottom: 20},
    tooltip: {
      trigger: 'axis',
      confine: true,
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#c5ddf5',
      textStyle: {fontSize: 11},
      formatter(params) {
        const lines = [`<b>${params[0].axisValue}</b>`];
        params.forEach((p) => {
          if (p.seriesName === '中标金额') {
            lines.push(`${p.marker}${p.seriesName}: ${formatWan(p.data * 10000)}`);
          } else {
            lines.push(`${p.marker}${p.seriesName}: ${p.data} 项`);
          }
        });
        return lines.join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: months,
      axisLine: {lineStyle: {color: '#eef4fc'}},
      axisTick: {show: false},
      axisLabel: {fontSize: 9, color: '#9cb8d8', margin: 6},
    },
    yAxis: [{
      type: 'value',
      name: '项',
      nameTextStyle: {fontSize: 9, color: '#9cb8d8', padding: [0, 24, 0, 0]},
      axisLine: {show: false},
      axisTick: {show: false},
      axisLabel: {fontSize: 9, color: '#9cb8d8'},
      splitLine: {lineStyle: {color: '#eef4fc', type: 'dashed'}},
    }, {
      type: 'value',
      name: '万元',
      nameTextStyle: {fontSize: 9, color: '#9cb8d8', padding: [0, 0, 0, 24]},
      axisLine: {show: false},
      axisTick: {show: false},
      axisLabel: {fontSize: 9, color: '#9cb8d8'},
      splitLine: {show: false},
    }],
    series: [{
      name: '参与投标数',
      type: 'line',
      yAxisIndex: 0,
      smooth: 0.35,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: false,
      lineStyle: {width: 1.2, color: '#1a6fd4'},
      itemStyle: {color: '#1a6fd4'},
      data: bidCounts,
    }, {
      name: '中标数',
      type: 'line',
      yAxisIndex: 0,
      smooth: 0.35,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: false,
      lineStyle: {width: 1.2, color: '#d97706'},
      itemStyle: {color: '#d97706'},
      data: winCounts,
    }, {
      name: '中标金额',
      type: 'bar',
      yAxisIndex: 1,
      barWidth: '36%',
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {offset: 0, color: 'rgba(110, 176, 245, 0.85)'},
          {offset: 1, color: 'rgba(26, 111, 212, 0.35)'},
        ]),
        borderRadius: [2, 2, 0, 0],
      },
      data: winAmounts,
    }],
  };
}

export function buildChartOption(type, items) {
  const list = items || [];
  const title = TITLES[type] || '';

  if (['scale', 'type', 'result'].includes(type)) {
    return pieOption(list, title);
  }
  if (type === 'trend') {
    return trendComboOption(list, title);
  }
  return pieOption(list, title);
}

export {TITLES, CHART_COLORS};
